import { Button, message, Tabs, Tooltip } from 'antd';
import { LinkOutlined, TeamOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useState } from 'react';
import { serverFetch } from '@server';
import { showError } from '@extensions/utils';
import ResizableDrawer from '@controls/resizable-drawer/resizable-drawer';
import DialogMessages from './messages';
import DialogMain from './dialog-main';
import { dialogTitle } from './dialog-utils';

// Карточка диалога в ResizableDrawer: вкладки «Сообщения» и «Карточка»
// registerRealtime(fn) — родитель подключает обработчик SignalR (новые сообщения)
const DialogDrawer = ({ id, onClose, onChanged, onOpenContragent, onCreatedTask, registerRealtime, registerDialogChanged, registerMessageDeleted }) => {
    const [dialog, setDialog] = useState(null);
    const [messages, setMessages] = useState([]);

    const load = useCallback(() => {
        serverFetch(`dialog/${id}`)
            .then(setDialog)
            .catch(showError);
        serverFetch(`dialog/${id}/messages`)
            .then(setMessages)
            .catch(showError);
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Новые сообщения по SignalR добавляем в ленту открытого диалога
    useEffect(() => {
        registerRealtime?.((dto) => {
            if (dto?.dialogId !== id) return;
            // статус исходящего обновляется (Sending → Sent/Error) — заменяем по id
            setMessages((l) => (l.some((m) => m.id === dto.id)
                ? l.map((m) => (m.id === dto.id ? { ...m, ...dto } : m))
                : [...l, dto]));
        });
        return () => registerRealtime?.(null);
    }, [id, registerRealtime]);

    useEffect(() => {
        registerMessageDeleted?.((dto) => {
            if (Number(dto?.dialogId) !== Number(id)) return;
            setMessages((l) => l.filter((m) => m.id !== dto.messageId));
        });
        return () => registerMessageDeleted?.(null);
    }, [id, registerMessageDeleted]);

    useEffect(() => {
        registerDialogChanged?.((dto) => {
            if (Number(dto?.dialogId) !== Number(id)) return;
            serverFetch(`dialog/${id}`).then(setDialog).catch(() => {});
        });
        return () => registerDialogChanged?.(null);
    }, [id, registerDialogChanged]);

    const onReadChanged = () => {
        serverFetch(`dialog/${id}`)
            .then(setDialog)
            .catch(() => {});
        onChanged?.();
    };

    return (
        <ResizableDrawer
            open
            storageKey="dialogDrawerWidth"
            title={dialog ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {dialog.isGroup ? <Tooltip title="Групповой чат"><TeamOutlined className="dlg-group-icon" /></Tooltip> : null}
                    {`Диалог: ${dialogTitle(dialog)}`}
                    <Tooltip title="Копировать ссылку">
                        <Button
                            type="text" size="small" icon={<LinkOutlined />}
                            onClick={() => {
                                navigator.clipboard?.writeText(`${window.location.origin}/dialogs?id=${id}`);
                                message.success('Ссылка скопирована');
                            }}
                        />
                    </Tooltip>
                </span>
            ) : 'Диалог'}
            onClose={() => onClose?.()}
            destroyOnClose
            className="resizable-drawer task-drawer"
        >
            {dialog && (
                <div className="task-scroll ca-scroll">
                    <Tabs
                        size="small"
                        className="ca-tabs"
                        destroyOnHidden
                        items={[
                            {
                                key: 'messages',
                                label: 'Сообщения',
                                children: (
                                    <DialogMessages
                                        dialog={dialog}
                                        messages={messages}
                                        setMessages={setMessages}
                                        onReadChanged={onReadChanged}
                                        onOpenContragent={onOpenContragent}
                                    />
                                ),
                            },
                            {
                                key: 'card',
                                label: 'Карточка',
                                children: (
                                    <DialogMain
                                        dialog={dialog}
                                        onSaved={(d) => { setDialog(d); onChanged?.(); }}
                                        onOpenContragent={onOpenContragent}
                                        onCreatedTask={onCreatedTask}
                                        onArchived={() => { onChanged?.(); onClose?.(); }}
                                        onClose={onClose}
                                    />
                                ),
                            },
                        ]}
                    />
                </div>
            )}
        </ResizableDrawer>
    );
};

export default DialogDrawer;
